// The retail park side of the page: its steps in the left panel, its options,
// its masterplan in 3D, its numbers, its report and its exploration. The plot,
// the rules, the setbacks, the scene and the saving are the residential
// page's own, handed in as a context, so the two modes share one import, one
// envelope and one share link.
import * as THREE from "three";
import type { MultiPoly, Pt, Ring } from "../domain/geom.ts";
import { limits } from "../domain/indicators.ts";
import type { Sheet } from "../domain/export/xlsx.ts";
import type { Rules, Site } from "../domain/rules.ts";
import { generateRetail, type RetailGenerated, type RetailResult } from "../domain/retail/generate.ts";
import { KIND_NAME, type RetailKind, type RetailProgramme } from "../domain/retail/programme.ts";
import {
  retailProForma, retailResidualAtYield, retailResidualLand, retailSensitivity, type RetailFinanceInputs, type RetailProForma,
} from "../domain/retail/finance.ts";
import { optionName, retailDxf, retailIfc, retailOptionsCsv, retailOptionsSheet, unitsCsv, unitsSheet, TYPOLOGY_NAME } from "../domain/retail/export.ts";
import { unitPlanSvg, unitSummary } from "../domain/retail/unitplan.ts";
import type { RetailUnit } from "../domain/retail/masterplan.ts";
import type { Envelope } from "../domain/envelope.ts";

export type RetailSort = "gla" | "profit" | "yoc" | "parking" | "units";

export interface RetailPageState {
  site: Site;
  rules: Rules;
  retail: RetailProgramme;
  retailFinance: RetailFinanceInputs;
  retailSort: RetailSort;
  targetMargin: number;
  targetYoc: number;
  optionKey: string;
  level: number;
  view: "3d" | "plan";
  envelope: boolean;
}

interface Num { label: string; hint?: string; get: (s: any) => [object, string]; min: number; max: number; step: number; unit?: string; show?: (v: number) => string }

export interface Ctx {
  state: RetailPageState;
  ALL: number;
  commit: string;
  brand: { name: string; logo?: string };
  $: <T extends HTMLElement = HTMLElement>(s: string) => T;
  fmt: (n: number, dp?: number) => string;
  money: (n: number) => string;
  css: (n: number) => string;
  esc: (t: string) => string;
  seg: <T extends string | number>(options: [T, string][], value: T, onPick: (v: T) => void) => HTMLElement;
  numField: (spec: Num) => HTMLElement;
  schedule: () => void;
  world: THREE.Group;
  prism: (mp: MultiPoly, base: number, h: number, m: THREE.Material, edges?: boolean) => THREE.Group;
  mat: (colour: number, opacity?: number) => THREE.Material;
  outline: (ring: Ring, y: number, colour: number) => THREE.Line;
  label: (html: string, cls: string, at: THREE.Vector3) => void;
  marker: (p: [number, number], y: number, colour: number, r?: number, h?: number) => void;
  segBar: (a: [number, number], b: [number, number], y: number, h: number, w: number, colour: number) => void;
  centroid: (mp: MultiPoly) => [number, number];
  tag: <T extends THREE.Object3D>(o: T, id: string) => T;
  resetScene: () => void;
  paintSite: () => void;
  frameCamera: () => void;
  capture: (level: number, view: "3d" | "plan") => string;
  download: (name: string, text: string, type: string) => void;
  saveXlsx: (file: string, sheet: Sheet) => void;
  printHtml: (body: string) => void;
  caseName: () => string;
  origin: () => Pt;
  imported: () => boolean;
}

let ctx: Ctx;
let result: RetailResult | null = null;
let current: RetailGenerated | null = null;
export const init = (c: Ctx) => { ctx = c; };
export const currentOption = () => current;
export const currentResult = () => result;

// ---------- colours ----------

export const RETAIL_COLOUR: Record<RetailKind | "pad", number> = { anchor: 0xe07a3f, medium: 0x3a86c8, small: 0x8ecae6, pad: 0x9b6bd6 };
const C = {
  yard: 0x8b929c, road: 0x9ca3af, access: 0x6b7280, aisle: 0xa8aeb7, stall: 0xf3f4f6, accessible: 0x3b82f6, ev: 0x22c55e,
  island: 0x6fbf73, green: 0x9bd39b, canopy: 0xcbd5e1, glass: 0x2c4a66, dock: 0x7c2d12, door: 0xd97706, column: 0x374151, pylon: 0x111827, lane: 0xb0b6be,
};

// ---------- the left panel ----------

const at = (obj: any, key: string): [object, string] => [obj, key];

/** The retail steps, after Teren, Reguli and Retrageri. `section` opens a new folding step and returns its box. */
export function steps(section: (title: string, hint?: string, open?: boolean) => HTMLElement) {
  const { state, numField, seg, schedule } = ctx;
  let box = section("Clădirile", "hale, curte, copertină");
  const BUILD: Num[] = [
    { label: "Înălțime la cornișă", hint: "hală de un nivel, ~7 m liber", get: (s) => at(s.retail, "height"), min: 5, max: 14, step: 0.5, unit: "m" },
    { label: "Curte de livrare", hint: "în spatele halelor, de la limită; TIR: 30 m", get: (s) => at(s.retail, "serviceDepth"), min: 10, max: 45, step: 1, unit: "m" },
    { label: "Copertină", hint: "peste trotuarul din fața magazinelor; intră în POT", get: (s) => at(s.retail, "canopyDepth"), min: 0, max: 6, step: 0.5, unit: "m" },
    { label: "Alee în fața magazinelor", hint: "circulație, pompieri, deposedare", get: (s) => at(s.retail, "frontRoad"), min: 4, max: 10, step: 0.5, unit: "m" },
    { label: "Travee structurală", hint: "pereții dintre unități cad pe jumătăți de travee", get: (s) => at(s.retail, "bay"), min: 6, max: 18, step: 1, unit: "m" },
  ];
  BUILD.forEach((s) => box.appendChild(numField(s)));
  const depth = document.createElement("div");
  depth.innerHTML = `<div class="field"><label>Adâncimea rândului<small>se încearcă fiecare; unitățile diferă prin lățime</small></label><span></span></div>
    <input type="text" id="rDepths" value="${state.retail.depths.join(", ")}">`;
  box.appendChild(depth);
  (depth.querySelector("#rDepths") as HTMLInputElement).addEventListener("input", (e) => {
    const ds = (e.target as HTMLInputElement).value.split(/[,; ]+/).map(Number).filter((d) => Number.isFinite(d) && d >= 15 && d <= 80);
    if (ds.length) { state.retail.depths = [...new Set(ds)].slice(0, 6); schedule(); }
  });

  box = section("Chiriași", "ancore, unități, pad-uri");
  const mix = document.createElement("table");
  mix.className = "mix";
  mix.innerHTML = `<tr><th>Tip</th><th>Pondere GLA %</th><th>Suprafață m²</th></tr>` + state.retail.mix.map((m, i) => `
    <tr><td><span class="swatch" style="background:${ctx.css(RETAIL_COLOUR[m.kind])}"></span>${KIND_NAME[m.kind]}</td>
    <td><input type="number" data-i="${i}" data-k="share" min="0" max="100" step="5" value="${Math.round(m.share * 100)}"></td>
    <td><input type="number" data-i="${i}" data-k="area" min="80" max="12000" step="50" value="${m.area}"></td></tr>`).join("");
  box.appendChild(mix);
  mix.querySelectorAll("input").forEach((inp) => inp.addEventListener("input", () => {
    const i = +inp.dataset.i!, v = parseFloat(inp.value);
    if (!Number.isFinite(v)) return;
    if (inp.dataset.k === "share") state.retail.mix[i].share = v / 100; else state.retail.mix[i].area = v;
    schedule();
  }));
  const TEN: Num[] = [
    { label: "Pad-uri drive-thru", hint: "restaurante pe front, fiecare cu lotul lui", get: (s) => at(s.retail, "pads"), min: 0, max: 6, step: 1 },
    { label: "Suprafață pad", get: (s) => at(s.retail, "padArea"), min: 150, max: 800, step: 10, unit: "m²" },
    { label: "GLA maxim dorit", hint: "0 = cât încape", get: (s) => at(s.retail, "maxGla"), min: 0, max: 60000, step: 500, unit: "m²" },
  ];
  TEN.forEach((s) => box.appendChild(numField(s)));

  box = section("Parcare și sit", "normă, țintă, verde");
  const PK: Num[] = [
    { label: "Ținta chiriașilor", hint: "locuri la 100 m² GLA; ancorele cer de obicei 4–5", get: (s) => at(s.retail, "parkingTarget"), min: 1, max: 8, step: 0.1 },
    { label: "Fâșie plantată la stradă", get: (s) => at(s.retail, "streetBuffer"), min: 0, max: 20, step: 0.5, unit: "m" },
    { label: "Un arbore la", hint: "locuri pe rând; 0 = fără insule", get: (s) => at(s.retail, "treeEvery"), min: 0, max: 20, step: 1, show: (v) => (v ? `${v} locuri` : "fără") },
  ];
  PK.forEach((s) => box.appendChild(numField(s)));
  const norm = document.createElement("div");
  norm.innerHTML = `
    <label class="check"><input type="checkbox" id="rNorm" ${state.rules.parkingPerGla !== undefined ? "checked" : ""}> Normă din PUZ / regulament local<small>altfel treptele RGU (HG 525/1996, Anexa 5), neverificate</small></label>
    <div class="field" id="rNormBox" ${state.rules.parkingPerGla !== undefined ? "" : "hidden"}><label>Locuri la 100 m² GLA</label><input type="number" id="rNormV" min="0" max="10" step="0.1" value="${state.rules.parkingPerGla ?? 2.5}"></div>
    <div class="field"><label>Spațiu verde minim<small>din PUZ; % din teren</small></label><input type="number" id="rGreen" min="0" max="60" step="1" value="${state.rules.minGreen ?? 0}"></div>`;
  box.appendChild(norm);
  (norm.querySelector("#rNorm") as HTMLInputElement).onchange = (e) => {
    const on = (e.target as HTMLInputElement).checked;
    (norm.querySelector("#rNormBox") as HTMLElement).hidden = !on;
    state.rules.parkingPerGla = on ? parseFloat((norm.querySelector("#rNormV") as HTMLInputElement).value) || 2.5 : undefined;
    schedule();
  };
  (norm.querySelector("#rNormV") as HTMLInputElement).addEventListener("input", (e) => { const v = parseFloat((e.target as HTMLInputElement).value); if (Number.isFinite(v)) { state.rules.parkingPerGla = v; schedule(); } });
  (norm.querySelector("#rGreen") as HTMLInputElement).addEventListener("input", (e) => { const v = parseFloat((e.target as HTMLInputElement).value); state.rules.minGreen = Number.isFinite(v) && v > 0 ? v : undefined; schedule(); });

  box = section("Bani", "chirii, yield, costuri");
  const F = state.retailFinance;
  const fin = document.createElement("div");
  fin.innerHTML = `<div class="sub">Valori orientative, nu date de piață: pune-le pe ale tale. Banii se recalculează fără a regenera planul.</div>`;
  const fnum = (label: string, hint: string, get: () => number, set: (v: number) => void, step: number) => {
    const el = document.createElement("div");
    el.className = "field";
    el.innerHTML = `<label>${label}<small>${hint}</small></label><input type="number" step="${step}" value="${get()}">`;
    el.querySelector("input")!.addEventListener("input", (e) => { const v = parseFloat((e.target as HTMLInputElement).value); if (Number.isFinite(v)) { set(v); repaintMoney(); } });
    fin.appendChild(el);
  };
  for (const k of ["anchor", "medium", "small", "pad"] as const) fnum(`Chirie ${KIND_NAME[k].toLowerCase()}`, "€ / m² GLA / lună", () => F.rent[k], (v) => (F.rent[k] = v), 0.25);
  fnum("Neocupare", "% din chirii, pe un ciclu", () => F.vacancyPct, (v) => (F.vacancyPct = v), 0.5);
  fnum("Costuri nerecuperabile", "% din chirii", () => F.nonRecoverablePct, (v) => (F.nonRecoverablePct = v), 0.5);
  fnum("Yield de ieșire", "% la care cumpără un investitor", () => F.exitYield, (v) => (F.exitYield = v), 0.05);
  fnum("Costuri cumpărător", "% din valoare", () => F.purchaserPct, (v) => (F.purchaserPct = v), 0.5);
  fnum("Teren", "€, tot", () => F.land, (v) => (F.land = v), 10000);
  fnum("Construcție hale", "€ / m² construit (shell & core)", () => F.costShell, (v) => (F.costShell = v), 10);
  fnum("Construcție pad", "€ / m² construit", () => F.costPad, (v) => (F.costPad = v), 10);
  fnum("Amenajări exterioare", "€ / m² de teren liber: parcare, drumuri, utilități, verde", () => F.costExternal, (v) => (F.costExternal = v), 1);
  fnum("Costuri indirecte", "% din construcție", () => F.softPct, (v) => (F.softPct = v), 0.5);
  fnum("Neprevăzute", "% din construcție", () => F.contingencyPct, (v) => (F.contingencyPct = v), 0.5);
  fnum("Luni de chirie gratuită", "la prima închiriere", () => F.rentFreeMonths, (v) => (F.rentFreeMonths = v), 1);
  fnum("Contribuție la amenajare", "€ / m² GLA, către chiriași", () => F.fitOut, (v) => (F.fitOut = v), 5);
  fnum("Comision închiriere", "% din chiria pe primul an", () => F.lettingPct, (v) => (F.lettingPct = v), 1);
  fnum("Marjă țintă", "% din valoarea netă, pentru valoarea reziduală a terenului", () => state.targetMargin, (v) => (state.targetMargin = v), 1);
  fnum("Yield on cost țintă", "%, a doua valoare reziduală", () => state.targetYoc, (v) => (state.targetYoc = v), 0.1);
  box.appendChild(fin);
}

// ---------- generation ----------

const keyOf = (g: RetailGenerated) => g.id;
const pfCache = new WeakMap<RetailGenerated, RetailProForma>();
export function pf(g: RetailGenerated): RetailProForma {
  if (!pfCache.has(g)) pfCache.set(g, retailProForma(g, ctx.state.retailFinance));
  return pfCache.get(g)!;
}

function normalised(): RetailProgramme {
  const p = ctx.state.retail;
  const t = p.mix.reduce((x, m) => x + m.share, 0) || 1;
  return { ...p, mix: p.mix.filter((m) => m.share > 0).map((m) => ({ ...m, share: m.share / t })) };
}

export function run() {
  const t = performance.now();
  try { result = generateRetail(ctx.state.site, ctx.state.rules, normalised(), 6); } catch (e) { console.error(e); result = null; }
  const ms = performance.now() - t;
  sortOptions();
  const opts = result?.options ?? [];
  current = opts.find((g) => keyOf(g) === ctx.state.optionKey) ?? opts[0] ?? null;
  if (current) ctx.state.optionKey = keyOf(current);
  if (ctx.state.level !== ctx.ALL && ctx.state.level !== 0) ctx.state.level = ctx.ALL;
  paintOptions(ms);
  paintScene();
  paintResults();
}

function sortOptions() {
  if (!result) return;
  const key: Record<RetailSort, (g: RetailGenerated) => number> = {
    gla: (g) => g.gla, profit: (g) => pf(g).profit, yoc: (g) => pf(g).yieldOnCost, parking: (g) => g.parking.capacity, units: (g) => g.scheme.units.length,
  };
  result.options.sort((a, b) => key[ctx.state.retailSort](b) - key[ctx.state.retailSort](a));
}

function repaintMoney() {
  if (!result) return;
  for (const g of result.options) pfCache.delete(g);
  sortOptions();
  paintOptions(0);
  paintResults();
}

// ---------- options strip ----------

function paintOptions(ms: number) {
  const { $, fmt, money } = ctx;
  const root = $("#options");
  const opts = result?.options ?? [];
  if (!opts.length) {
    root.innerHTML = `<div class="empty">${result?.message ?? "Nicio variantă."} Încearcă o curte de livrare mai mică, un rând mai puțin adânc sau retrageri mai mici.</div>`;
    return;
  }
  const best = Math.max(...opts.map((g) => g.gla));
  root.innerHTML = opts.map((g, i) => `<button class="opt ${g === current ? "on" : ""}" data-i="${i}">
      <b>${TYPOLOGY_NAME[g.typology]}</b> · adâncime ${g.scheme.depth} m${g.typology === "line" && g.scheme.typology !== "line" ? `<div class="m">livrări pe ${g.scheme.typology === "L-left" ? "stânga" : g.scheme.typology === "L-right" ? "dreapta" : "laterale"}</div>` : ""}
      <div class="big">${fmt(g.gla)} m² GLA</div>
      <div class="m"><b class="${pf(g).profit >= 0 ? "pass" : "fail"}">${money(pf(g).profit)}</b> · yield on cost ${fmt(pf(g).yieldOnCost, 2)}%</div>
      <div class="m">${g.scheme.units.length} unități${g.scheme.pads.length ? ` + ${g.scheme.pads.length} pad-uri` : ""} · ${g.parking.capacity} locuri</div>
      <div class="m">POT ${fmt(g.indicators.pot, 1)}% · verde ${fmt((g.balance.green / g.balance.plot) * 100, 0)}% · limitat de ${g.binding.join(", ")}</div>
      <div class="bar"><i style="width:${(g.gla / best) * 100}%"></i></div>
    </button>`).join("") + `<div class="optbar">
      <label class="sub">Ordonează după
        <select id="rSortBy"><option value="gla">suprafață închiriabilă</option><option value="profit">profit</option><option value="yoc">yield on cost</option><option value="parking">locuri de parcare</option><option value="units">număr de unități</option></select></label>
      <button class="btn" id="rCompare">Compară variantele</button>
      <div class="timing">${ms ? `${fmt(ms)} ms` : ""}</div></div>`;
  const sel = $("#rSortBy") as HTMLSelectElement;
  sel.value = ctx.state.retailSort;
  sel.onchange = () => { ctx.state.retailSort = sel.value as RetailSort; sortOptions(); paintOptions(0); };
  $("#rCompare").onclick = showComparison;
  root.querySelectorAll<HTMLButtonElement>(".opt").forEach((b) => (b.onclick = () => {
    current = opts[+b.dataset.i!];
    ctx.state.optionKey = keyOf(current);
    paintOptions(ms); paintScene(); paintResults();
  }));
}

function showComparison() {
  if (!result) return;
  const { fmt, money } = ctx;
  const cols: { k: string; v: (g: RetailGenerated) => number; f: (n: number) => string; low?: boolean }[] = [
    { k: "GLA m²", v: (g) => g.gla, f: (n) => fmt(n) },
    { k: "Unități", v: (g) => g.scheme.units.length, f: (n) => fmt(n) },
    { k: "Locuri", v: (g) => g.parking.capacity, f: (n) => fmt(n) },
    { k: "Loc / 100 m²", v: (g) => (g.parking.capacity / Math.max(1, g.gla)) * 100, f: (n) => fmt(n, 1) },
    { k: "POT %", v: (g) => g.indicators.pot, f: (n) => fmt(n, 1) },
    { k: "Verde %", v: (g) => (g.balance.green / g.balance.plot) * 100, f: (n) => fmt(n, 1) },
    { k: "Chirii / an", v: (g) => pf(g).rentRoll, f: money },
    { k: "Cost", v: (g) => pf(g).cost.total, f: money, low: true },
    { k: "Profit", v: (g) => pf(g).profit, f: money },
    { k: "Yield on cost %", v: (g) => pf(g).yieldOnCost, f: (n) => fmt(n, 2) },
  ];
  let sortCol = 8, desc = true;
  const dlg = document.createElement("dialog");
  dlg.className = "compare";
  const paint = () => {
    const rows = [...result!.options].sort((a, b) => (desc ? -1 : 1) * (cols[sortCol].v(a) - cols[sortCol].v(b)));
    const best = cols.map((c) => (c.low ? Math.min : Math.max)(...rows.map(c.v)));
    dlg.innerHTML = `<div class="dhead"><b>Comparație variante</b><button class="btn" id="dclose">Închide</button></div>
      <table class="t"><tr><th>Variantă</th>${cols.map((c, i) => `<th data-c="${i}" class="${i === sortCol ? "on" : ""}">${c.k}${i === sortCol ? (desc ? " ↓" : " ↑") : ""}</th>`).join("")}</tr>
      ${rows.map((g) => `<tr data-k="${keyOf(g)}"><td>${optionName(g)}</td>${cols.map((c, i) => `<td class="n ${c.v(g) === best[i] ? "best" : ""}">${c.f(c.v(g))}</td>`).join("")}</tr>`).join("")}</table>
      <div class="sub">Click pe o coloană ca să ordonezi, pe un rând ca să deschizi varianta.</div>`;
    dlg.querySelector("#dclose")!.addEventListener("click", () => dlg.close());
    dlg.querySelectorAll<HTMLElement>("th[data-c]").forEach((th) => (th.onclick = () => { const c = +th.dataset.c!; desc = c === sortCol ? !desc : true; sortCol = c; paint(); }));
    dlg.querySelectorAll<HTMLElement>("tr[data-k]").forEach((tr) => (tr.onclick = () => {
      const g = result!.options.find((x) => keyOf(x) === tr.dataset.k);
      if (g) { current = g; ctx.state.optionKey = keyOf(g); paintOptions(0); paintScene(); paintResults(); }
      dlg.close();
    }));
  };
  paint();
  dlg.addEventListener("close", () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
}

// ---------- results ----------

function paintResults() {
  const { $, fmt, money, state } = ctx;
  const root = $("#results");
  if (!current || !result) { root.innerHTML = `<div class="empty">—</div>`; return; }
  const g = current, s = g.scheme, p = g.parking, f = pf(g), b = g.balance;
  const plotArea = result.envelope.plotArea;
  const kpi = (k: string, v: string) => `<div><span>${k}</span><b>${v}</b></div>`;
  const n = (v: string) => g.checks.filter((c) => c.verdict === v).length;
  const fail = n("fail"), todo = n("not-checked");
  const L = limits(state.rules, plotArea);
  const gauge = (k: string, used: number, max: number, unit = "", invert = false) => {
    const r = max ? used / max : 0;
    const cls = invert ? (r >= 1 - 1e-9 ? "good" : "over") : r > 1.0001 ? "over" : r >= 0.9 ? "good" : "under";
    return `<div class="gauge ${cls}"><div class="glabel"><span>${k}</span><b>${fmt(used, unit === "" ? 2 : 1)}${unit} / ${fmt(max, unit === "" ? 2 : 1)}${unit}</b></div><div class="gbar"><i style="width:${Math.min(100, r * 100)}%"></i></div></div>`;
  };
  const row = (k: string, v: string, note = "") => `<tr><td>${k}${note ? `<div class="src">${note}</div>` : ""}</td><td class="n">${v}</td></tr>`;
  const docks = s.units.flatMap((u) => u.service.filter((o) => o.kind === "dock"));
  const kinds = g.byKind;
  root.innerHTML = `
    <h2 style="margin-top:0">${optionName(g)} · ${fmt(g.height, 1)} m</h2>
    <div class="sub" style="margin-bottom:8px">Ce oprește rândul să crească: <b>${g.binding.join(", ")}</b>. ${s.trim < 1 ? `Rândul e scurtat la ${fmt(s.trim * 100, 0)}% din cât ar permite anvelopa.` : "Rândul ocupă toată anvelopa."}</div>
    <a class="verdict ${fail ? "fail" : "pass"}" href="#rCompliance">${fail ? `${fail} ${fail === 1 ? "regulă încălcată" : "reguli încălcate"}` : "Respectă toate regulile verificate"} · ${n("pass")} în regulă${todo ? ` · ${todo} de verificat de tine` : ""} ↓</a>
    <div class="gauges">
      ${gauge("POT", g.indicators.pot, L.potLimit, "%")}${gauge("CUT", g.indicators.cut, L.cutLimit)}
      ${gauge("Parcare (asigurat / necesar)", p.capacity, p.required, "", true)}
      ${state.rules.minGreen ? gauge("Spațiu verde (min)", (b.green / b.plot) * 100, state.rules.minGreen, "%", true) : ""}
    </div>
    <div class="kpis">
      ${kpi("Teren", fmt(plotArea) + " m²")}${kpi("GLA", fmt(g.gla) + " m²")}
      ${kpi("Construit (Ac)", fmt(g.footprintArea) + " m²")}${kpi("Unități", `${s.units.length}${s.pads.length ? ` + ${s.pads.length} pad` : ""}`)}
      ${kpi("Locuri de parcare", `${p.capacity} · ${fmt((p.capacity / Math.max(1, g.gla)) * 100, 1)}/100 m²`)}${kpi("Yield on cost", fmt(f.yieldOnCost, 2) + "%")}
      ${kpi("Chirii / an", money(f.rentRoll))}${kpi("Profit", money(f.profit))}
    </div>
    <h2>Chiriași</h2>
    <table class="t">${kinds.map((k) => `<tr><td><span class="swatch" style="background:${ctx.css(RETAIL_COLOUR[k.kind])}"></span>${KIND_NAME[k.kind]}</td><td class="n">${k.count}</td><td class="n src">${fmt(k.gla)} m² · medie ${fmt(k.gla / k.count)} m²</td></tr>`).join("")}
      <tr><td><b>Total</b></td><td class="n">${s.units.length + s.pads.length}</td><td class="n src">${fmt(g.gla)} m² GLA</td></tr></table>
    <details class="box"><summary>Toate unitățile (${s.units.length + s.pads.length})</summary>
      <table class="t">${s.units.map((u) => `<tr data-u="${u.id}" style="cursor:pointer"><td><span class="swatch" style="background:${ctx.css(RETAIL_COLOUR[u.kind])}"></span>${u.id} · ${KIND_NAME[u.kind]}<div class="src">${fmt(u.frontage, 1)} × ${fmt(u.depth, 1)} m · ${u.service.filter((o) => o.kind === "dock").length} cheiuri</div></td><td class="n">${fmt(u.gla)} m²<div class="src">${money(u.gla * state.retailFinance.rent[u.kind] * 12)}/an</div></td></tr>`).join("")}
      ${s.pads.map((x) => `<tr><td><span class="swatch" style="background:${ctx.css(RETAIL_COLOUR.pad)}"></span>${x.id} · ${KIND_NAME.pad}<div class="src">${x.stalls.length} locuri proprii</div></td><td class="n">${fmt(x.gla)} m²<div class="src">${money(x.gla * state.retailFinance.rent.pad * 12)}/an</div></td></tr>`).join("")}</table>
      <div class="sub">Click pe o unitate ca să-i vezi planul.</div></details>
    <h2>Financiar</h2>
    <table class="t">
      ${f.byKind.map((k) => row(`Chirii ${KIND_NAME[k.kind].toLowerCase()}`, money(k.rent), `${fmt(k.gla)} m² × ${fmt(state.retailFinance.rent[k.kind], 2)} €/m²/lună`)).join("")}
      ${row("<b>Chirii pe an</b>", money(f.rentRoll))}
      ${row("NOI", money(f.noi), `după ${fmt(state.retailFinance.vacancyPct, 1)}% neocupare și ${fmt(state.retailFinance.nonRecoverablePct, 1)}% costuri nerecuperabile`)}
      ${row("<b>Valoare la yield</b>", money(f.gdv), `NOI / ${fmt(state.retailFinance.exitYield, 2)}% · net de costurile cumpărătorului ${money(f.netValue)}`)}
      ${row("Teren", money(f.cost.land))}${row("Hale", money(f.cost.shell))}${f.cost.pads ? row("Pad-uri", money(f.cost.pads)) : ""}
      ${row("Amenajări exterioare", money(f.cost.external), "parcare, drumuri, curți, utilități, verde")}
      ${row("Indirecte și neprevăzute", money(f.cost.soft + f.cost.contingency))}${row("Închiriere și stimulente", money(f.cost.letting + f.cost.incentives), "comision, chirie gratuită, contribuții la amenajare")}
      ${row("<b>Cost total</b>", money(f.cost.total), `${fmt(f.costPerGla)} € / m² GLA`)}
      <tr><td><b>Profit</b><div class="src">marjă ${fmt(f.margin * 100, 1)}% · pe cost ${fmt(f.onCost * 100, 1)}%</div></td><td class="n ${f.profit >= 0 ? "pass" : "fail"}">${money(f.profit)}</td></tr>
      <tr><td><b>Yield on cost</b><div class="src">NOI / cost total; față de yield-ul de ieșire de ${fmt(state.retailFinance.exitYield, 2)}%</div></td><td class="n ${f.yieldOnCost >= state.retailFinance.exitYield ? "pass" : "fail"}">${fmt(f.yieldOnCost, 2)}%</td></tr>
      ${row("<b>Cât poți plăti pe teren</b>", money(retailResidualLand(g, state.retailFinance, state.targetMargin / 100)), `la o marjă de ${fmt(state.targetMargin, 0)}% · ${fmt(retailResidualLand(g, state.retailFinance, state.targetMargin / 100) / plotArea, 0)} € / m² de teren`)}
      ${row("…la un yield on cost țintă", money(retailResidualAtYield(g, state.retailFinance, state.targetYoc)), `${fmt(state.targetYoc, 2)}% · ${fmt(retailResidualAtYield(g, state.retailFinance, state.targetYoc) / plotArea, 0)} € / m² de teren`)}
    </table>
    <table class="t sens">${(() => {
      const m = retailSensitivity(g, state.retailFinance);
      const y = state.retailFinance.exitYield;
      return `<tr><th>Profit dacă</th><th>yield ${fmt(y - 0.5, 2)}%</th><th>yield ${fmt(y, 2)}%</th><th>yield ${fmt(y + 0.5, 2)}%</th></tr>` +
        ["chirii −10%", "chirii așa", "chirii +10%"].map((k, i) => `<tr><td>${k}</td>${m[i].map((v) => `<td class="n ${v >= 0 ? "pass" : "fail"}">${money(v)}</td>`).join("")}</tr>`).join("");
    })()}</table>
    <h2>Export</h2>
    <div class="row"><button class="btn" id="rxDxf">DXF masterplan</button><button class="btn" id="rxIfc">IFC (BIM)</button></div>
    <div class="row" style="margin-top:6px"><button class="btn" id="rxUnits">Excel chiriași</button><button class="btn" id="rxOpts">Excel variante</button></div>
    <div class="row" style="margin-top:6px"><button class="btn" id="rxUnitsCsv">CSV chiriași</button><button class="btn" id="rxOptsCsv">CSV variante</button></div>
    <div class="sub" style="margin-top:4px">${ctx.imported() ? "DXF-ul e în Stereo 70, peste planul cadastral." : "DXF-ul e în coordonate locale; importă terenul ca să-l primești în Stereo 70."}</div>
    <h2>Bilanț teritorial</h2>
    <table class="t">${(() => {
      const pct = (x: number) => fmt((x / b.plot) * 100, 1) + "%";
      const r3 = (k: string, v: number, note = "") => `<tr><td>${k}${note ? `<div class="src">${note}</div>` : ""}</td><td class="n">${fmt(v)} m²</td><td class="n src">${pct(v)}</td></tr>`;
      return r3("Construit (hale și pad-uri)", b.built) + r3("Copertină", b.canopy, "trotuar acoperit, intră în POT") + r3("Parcare", b.parking, "locuri și alei") +
        r3("Drumuri", b.roads, "intrare, aleea din fața magazinelor, drive-thru") + r3("Curți de livrare", b.service) +
        r3("Spații verzi", b.green, `fâșia de la stradă, ${p.islands} insule cu arbori, ce rămâne`) + `<tr><td><b>Teren</b></td><td class="n">${fmt(b.plot)} m²</td><td class="n src">100%</td></tr>`;
    })()}</table>
    <h2>Parcare</h2>
    <table class="t">
      ${row("După normă", String(p.byLaw), p.fromRules ? `${fmt(state.rules.parkingPerGla!, 1)} / 100 m² GLA, din PUZ` : "treptele RGU pe suprafața construită (neverificat)")}
      ${row("Ținta chiriașilor", String(p.target), `${fmt(state.retail.parkingTarget, 1)} / 100 m² GLA`)}
      ${row("<b>Asigurat</b>", `<span class="${p.capacity >= p.required ? "pass" : "fail"}">${p.capacity}</span>`, `${p.onPads ? `din care ${p.onPads} pe loturile pad-urilor · ` : ""}${fmt(p.perSpace, 1)} m² de parcare și drum pe loc`)}
      ${row("Persoane cu dizabilități / încărcare electrică", `${p.accessible} / ${p.ev}`, "cele mai apropiate de intrări; neverificat")}
      ${row("Distanța de mers până la intrare", `max ${fmt(p.walkMax, 0)} m`, `median ${fmt(p.walkMedian, 0)} m, în linie dreaptă`)}
      ${p.converted ? row("Locuri date spațiului verde", String(p.converted), "pentru procentul de verde din PUZ") : ""}
    </table>
    <h2>Livrări</h2>
    <table class="t">
      ${row("Cheiuri de descărcare", String(docks.length), `${s.units.filter((u) => u.kind === "anchor").length} ancore cu câte 2, unitățile medii cu câte 1, cele mici cu ușă de serviciu`)}
      ${row("Curte liberă minimă", docks.length ? `${fmt(Math.min(...docks.map((o) => o.apron)), 1)} m` : "—", "în fața cheiurilor, până la limită sau la altă clădire")}
      ${row("Porți de serviciu din stradă", String(s.truckGates.length), "separate de intrarea clienților")}
    </table>
    <h2 id="rCompliance">Conformitate</h2>
    <table class="t">${g.checks.map((c) => `<tr><td>${c.rule}${c.verified ? "" : `<span class="unv" title="Regula nu a fost verificată în textul oficial">neverificat</span>`}<div class="src">${c.source}</div>${c.note ? `<div class="src">${c.note}</div>` : ""}</td><td class="n ${c.verdict}">${c.value}<div class="src">${c.limit}</div></td></tr>`).join("")}</table>`;
  root.querySelectorAll<HTMLElement>("tr[data-u]").forEach((tr) => (tr.onclick = () => { const u = s.units.find((x) => x.id === tr.dataset.u); if (u) showUnit(u); }));
  const name = slug();
  const origin = ctx.origin();
  $("#rxDxf").onclick = () => ctx.download(`${name}-${g.id}.dxf`, retailDxf(g, result!.envelope, origin), "application/dxf");
  $("#rxIfc").onclick = () => ctx.download(`${name}-${g.id}.ifc`, retailIfc(g, ctx.caseName(), origin), "application/x-step");
  $("#rxUnitsCsv").onclick = () => ctx.download(`${name}-${g.id}-chiriasi.csv`, unitsCsv(g, state.retailFinance), "text/csv");
  $("#rxOptsCsv").onclick = () => ctx.download(`${name}-variante.csv`, retailOptionsCsv(result!.options.map((x) => ({ g: x, pf: pf(x) })), plotArea), "text/csv");
  const pre = [`${ctx.caseName()} · ${optionName(g)} · teren ${fmt(plotArea)} m²`, `Development Assistant ${ctx.commit}`];
  $("#rxUnits").onclick = () => ctx.saveXlsx(`${name}-${g.id}-chiriasi.xlsx`, unitsSheet(g, state.retailFinance, pre));
  $("#rxOpts").onclick = () => ctx.saveXlsx(`${name}-variante.xlsx`, retailOptionsSheet(result!.options.map((x) => ({ g: x, pf: pf(x) })), pre));
}

const slug = () => ctx.caseName().replace(/[^\p{L}\p{N}]+/gu, "-").toLowerCase();

// ---------- scene ----------

export function paintScene() {
  const { world, prism, mat, outline, label, marker, segBar, centroid, state, fmt } = ctx;
  ctx.resetScene();
  if (!result) { paintLevels(); paintLegend(); paintFloorCard(); return; }
  ctx.paintSite();
  const g = current;
  if (state.envelope && result.envelope.storeys[0]) {
    const env = result.envelope.storeys[0];
    world.add(prism(env.footprint, 0, state.rules.maxHeight, mat(0xf59e0b, 0.1), false));
    for (const p of env.footprint) world.add(outline(p[0], state.rules.maxHeight, 0xb45309));
  }
  if (!g) { paintLevels(); paintLegend(); paintFloorCard(); return; }
  const s = g.scheme;
  const plan = state.level === 0;
  const H = g.height;
  const flat = (mp: MultiPoly, colour: number, y = 0.03, h = 0.05) => { if (mp.length) world.add(prism(mp, y, h, mat(colour), false)); };
  flat(s.green, C.green, 0.03, 0.04);
  flat(s.service, C.yard); flat(s.lanes, C.yard); flat(s.frontRoad, C.road); flat(s.access, C.access, 0.03, 0.07);
  flat(s.parking.aisles, C.aisle);
  const acc = new Set(s.parking.accessible), ev = new Set(s.parking.ev);
  s.parking.stalls.forEach((st, i) => {
    world.add(prism(st, 0.03, 0.07, mat(acc.has(i) ? C.accessible : ev.has(i) ? C.ev : C.stall), false));
    world.add(outline(st[0][0], 0.11, 0x6b7280));
  });
  // a tree on every planted island: a trunk and a round crown
  const trunk = mat(0x7c5a3a), crown = mat(0x4f9a52);
  for (const st of s.parking.islands) {
    flat(st, C.island, 0.03, 0.12);
    const [x, y] = centroid(st);
    const t = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 2.4, 8), trunk); t.position.set(x, 1.2, -y); world.add(t);
    const c = new THREE.Mesh(new THREE.SphereGeometry(1.4, 12, 10), crown); c.position.set(x, 3.1, -y); world.add(c);
  }
  // pads: the lot, the drive-through lane, the building
  for (const p of s.pads) {
    flat(p.lane, C.lane, 0.03, 0.08);
    flat(p.aisles, C.aisle);
    for (const st of p.stalls) { world.add(prism(st, 0.03, 0.07, mat(C.stall), false)); world.add(outline(st[0][0], 0.11, 0x6b7280)); }
    for (const q of p.lot) world.add(outline(q[0], 0.12, 0x9b6bd6));
    world.add(prism(p.building, 0, plan ? 0.2 : 5.5, mat(RETAIL_COLOUR.pad), true));
    const [x, y] = centroid(p.building);
    label(`<b>${p.id}</b> drive-thru<br>${fmt(p.gla)} m²`, "", new THREE.Vector3(x, plan ? 1.4 : 7, -y));
  }
  // the row
  for (const u of s.units) {
    const colour = RETAIL_COLOUR[u.kind];
    if (plan) {
      world.add(ctx.tag(prism(u.polygon, 0, 0.2, mat(colour), false), u.id));
      for (const q of u.polygon) world.add(outline(q[0], 0.3, 0x111827));
    } else {
      world.add(ctx.tag(prism(u.polygon, 0, H, mat(colour), true), u.id));
    }
    // shopfront glazing and the service doors
    segBar(u.front[0], u.front[1], 0, plan ? 0.35 : 4.5, plan ? 0.35 : 0.12, C.glass);
    for (const o of u.service) segBar(o.a, o.b, 0, plan ? 0.35 : o.kind === "dock" ? 3.6 : 2.4, plan ? 0.4 : 0.14, o.kind === "dock" ? C.dock : C.door);
    const [x, y] = centroid(u.polygon);
    label(`<b>${u.id}</b> ${KIND_NAME[u.kind].toLowerCase()}<br>${fmt(u.gla)} m²`, "", new THREE.Vector3(x, plan ? 1.2 : H + 1.2, -y));
  }
  // the canopy over the walkway, on posts
  if (s.canopy.length) world.add(prism(s.canopy, plan ? 0.25 : 4.6, plan ? 0.05 : 0.3, mat(C.canopy, plan ? 0.6 : 1), !plan));
  if (plan) {
    const m = mat(C.column);
    for (const c of s.columns) world.add(prism([[[[c[0] - 0.25, c[1] - 0.25], [c[0] + 0.25, c[1] - 0.25], [c[0] + 0.25, c[1] + 0.25], [c[0] - 0.25, c[1] + 0.25]]]], 0, 1.4, m, false));
  }
  // entrances and the pylon
  marker(s.carAccess, 0.05, 0xb45309, 1.2, 0.5);
  label("Intrare clienți", "ramp", new THREE.Vector3(s.carAccess[0], 2.5, -s.carAccess[1]));
  for (const q of s.truckGates) { marker(q, 0.05, 0x374151, 1.2, 0.5); label("Livrări", "core", new THREE.Vector3(q[0], 2.5, -q[1])); }
  const [px, py] = s.pylon;
  const f = s.streetFrame;
  const tot: MultiPoly = [[[[px - f.u[0] * 1.2 - f.n[0] * 0.3, py - f.u[1] * 1.2 - f.n[1] * 0.3], [px + f.u[0] * 1.2 - f.n[0] * 0.3, py + f.u[1] * 1.2 - f.n[1] * 0.3], [px + f.u[0] * 1.2 + f.n[0] * 0.3, py + f.u[1] * 1.2 + f.n[1] * 0.3], [px - f.u[0] * 1.2 + f.n[0] * 0.3, py - f.u[1] * 1.2 + f.n[1] * 0.3]]]];
  world.add(prism(tot, 0, plan ? 0.4 : 12, mat(C.pylon), false));
  if (!plan) label("Totem", "core", new THREE.Vector3(px, 13, -py));
  paintLevels(); paintLegend(); paintFloorCard();
}

function paintLevels() {
  const { $, state, ALL } = ctx;
  const root = $("#levels");
  if (!current) { root.innerHTML = ""; return; }
  const btn = (i: number, t: string) => `<button data-l="${i}" class="${state.level === i ? "on" : ""}">${t}</button>`;
  root.innerHTML = btn(ALL, "Tot") + btn(0, "P");
  root.querySelectorAll<HTMLButtonElement>("button").forEach((b) => (b.onclick = () => { state.level = +b.dataset.l!; ctx.frameCamera(); paintScene(); }));
}

function paintLegend() {
  const { $, css, state } = ctx;
  const row = (c: number, t: string) => `<div><span class="swatch" style="background:${css(c)}"></span>${t}</div>`;
  const used = new Set<string>((current?.scheme.units ?? []).map((u) => u.kind));
  if (current?.scheme.pads.length) used.add("pad");
  const plan = state.level === 0;
  const html = (["anchor", "medium", "small", "pad"] as const).filter((k) => used.has(k)).map((k) => row(RETAIL_COLOUR[k], KIND_NAME[k])).join("") +
    row(C.canopy, "Copertină") + row(C.glass, "Vitrină") + row(C.dock, "Cheu de descărcare") + row(C.door, "Ușă de serviciu") +
    (plan ? row(C.column, "Stâlpi") : "") + row(C.yard, "Curte de livrare") + row(C.road, "Alee în fața magazinelor") + row(C.access, "Intrare clienți") +
    row(C.stall, "Loc de parcare 2,5 × 5 m") + row(C.accessible, "Loc pentru persoane cu dizabilități") + row(C.ev, "Loc cu încărcare electrică") +
    row(C.island, "Insulă cu arbore") + row(C.green, "Spațiu verde") + (state.envelope ? row(0xf59e0b, "Volum permis") : "");
  const el = $("#legend");
  let open = window.innerWidth > 760;
  try { const v = localStorage.getItem("da:legend"); if (v !== null) open = v === "1"; } catch { /* private window */ }
  el.innerHTML = `<details ${open ? "open" : ""}><summary>Legendă</summary><div class="lrows">${html}</div></details>`;
  el.querySelector("details")!.addEventListener("toggle", (e) => { try { localStorage.setItem("da:legend", (e.target as HTMLDetailsElement).open ? "1" : "0"); } catch { /* private window */ } });
}

function paintFloorCard() {
  const { $, fmt, state, ALL } = ctx;
  const root = $("#floorcard");
  if (!current || state.level === ALL) { root.innerHTML = ""; return; }
  const s = current.scheme;
  root.innerHTML = `<div class="src">Parter · cornișă +${fmt(current.height, 2)} · travee ${fmt(state.retail.bay, 0)} m</div>
    <b>${s.units.length} unități · ${fmt(s.units.reduce((t, u) => t + u.gla, 0))} m² GLA</b>
    <div class="src">${current.byKind.map((k) => `${k.count} × ${KIND_NAME[k.kind].toLowerCase()}`).join(" · ")}</div>
    <button class="btn" id="rUnitPlans" style="width:100%;margin-top:6px">Planuri unități</button><div class="src" style="margin-top:4px">sau click pe o unitate</div>`;
  root.querySelector<HTMLButtonElement>("#rUnitPlans")!.onclick = () => showUnit(s.units[0]);
}

/** A click in the scene, on a unit's id. */
export function pick(id: string): boolean {
  const u = current?.scheme.units.find((x) => x.id === id);
  if (u) showUnit(u);
  return !!u;
}

// ---------- one unit ----------

function showUnit(start: RetailUnit) {
  if (!current) return;
  const { fmt, money, state } = ctx;
  const s = current.scheme;
  let u = start;
  const dlg = document.createElement("dialog");
  dlg.className = "compare flatplan";
  const paint = () => {
    const k = s.units.indexOf(u);
    const svg = unitPlanSvg(u, s, state.retail);
    const rent = state.retailFinance.rent[u.kind];
    dlg.innerHTML = `<div class="dhead"><b>Unitatea ${u.id} · ${KIND_NAME[u.kind]}</b>
        <span class="row" style="gap:6px">
          <button class="btn" id="uprev" ${k <= 0 ? "disabled" : ""}>←</button><span class="src">${k + 1} / ${s.units.length}</span>
          <button class="btn" id="unext" ${k >= s.units.length - 1 ? "disabled" : ""}>→</button><button class="btn" id="dclose">Închide</button>
        </span></div>
      <div class="fp"><div class="fpsvg">${svg}</div>
        <div class="fpside">
          <table class="t">${unitSummary(u).map((x) => `<tr><td>${x.label}</td><td class="n">${x.value}</td></tr>`).join("")}
            <tr><td>Chirie</td><td class="n">${fmt(rent, 2)} €/m²/lună</td></tr>
            <tr><td><b>Chirie pe an</b></td><td class="n">${money(u.gla * rent * 12)}</td></tr></table>
          <div class="row" style="margin-top:10px"><button class="btn" id="usvg">Descarcă SVG</button><button class="btn" id="uprint">Tipărește</button></div>
          <div class="sub" style="margin-top:10px">Hală de un nivel pe o structură cu travee de ${fmt(state.retail.bay, 0)} m și deschideri de cel mult 25 m. Pereții dintre unități cad pe jumătăți de travee; compartimentarea interioară o face chiriașul.</div>
        </div></div>`;
    dlg.querySelector("#dclose")!.addEventListener("click", () => dlg.close());
    dlg.querySelector<HTMLButtonElement>("#uprev")!.onclick = () => { u = s.units[k - 1]; paint(); };
    dlg.querySelector<HTMLButtonElement>("#unext")!.onclick = () => { u = s.units[k + 1]; paint(); };
    dlg.querySelector<HTMLButtonElement>("#usvg")!.onclick = () => ctx.download(`unitatea-${u.id}.svg`, svg, "image/svg+xml");
    dlg.querySelector<HTMLButtonElement>("#uprint")!.onclick = () => ctx.printHtml(`<h1 style="font:600 18px system-ui;margin:0 0 4px">Unitatea ${u.id} · ${KIND_NAME[u.kind]}</h1>
      <div style="font:12px system-ui;color:#555;margin-bottom:10px">${ctx.esc(ctx.caseName())} · ${optionName(current!)} · Development Assistant ${ctx.commit}</div>
      ${svg.replace(/width="\d+" height="\d+"/, 'style="width:100%;height:auto;max-height:200mm"')}
      <table style="font:12px system-ui;border-collapse:collapse;margin-top:8px">${unitSummary(u).map((x) => `<tr><td style="padding:2px 16px 2px 0">${x.label}</td><td style="text-align:right">${x.value}</td></tr>`).join("")}</table>`);
  };
  paint();
  dlg.addEventListener("close", () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
}

// ---------- exploration ----------

interface Trial { label: string; prog: RetailProgramme; g: RetailGenerated; pf: RetailProForma }
let exploring = false;

/**
 * The programme choices a retail developer tries one by one, all at once:
 * the depth of the row, the depth of the yard, how many pads. The plot and
 * the rules never vary.
 */
export async function explore(btn: HTMLElement) {
  if (exploring) { exploring = false; return; }
  exploring = true;
  const base = normalised();
  const grid: { label: string; prog: RetailProgramme }[] = [];
  for (const d of [30, 38, 45, 55]) for (const sd of [20, 30]) for (const pads of [0, 2, 4]) {
    grid.push({ label: `rând ${d} m · curte ${sd} m · ${pads} pad-uri`, prog: { ...base, depths: [d], serviceDepth: sd, pads } });
  }
  const trials: Trial[] = [];
  for (let i = 0; i < grid.length && exploring; i++) {
    btn.textContent = `Oprește (${i + 1} / ${grid.length})`;
    await new Promise((r) => setTimeout(r, 0));
    try {
      const r = generateRetail(ctx.state.site, ctx.state.rules, grid[i].prog, 2);
      for (const g of r.options) trials.push({ label: grid[i].label, prog: grid[i].prog, g, pf: retailProForma(g, ctx.state.retailFinance) });
    } catch (e) { console.error(e); }
  }
  exploring = false;
  btn.textContent = "Explorează automat";
  const seen = new Set<string>();
  const pool = trials.filter((t) => { const k = `${t.g.id}|${Math.round(t.g.gla)}|${t.g.parking.capacity}|${t.g.scheme.pads.length}`; return seen.has(k) ? false : (seen.add(k), true); });
  showExploration(pool, trials.length);
}

function showExploration(pool: Trial[], tried: number) {
  const { fmt, money } = ctx;
  const SHOW = 10;
  const fails = (t: Trial) => t.g.checks.filter((c) => c.verdict === "fail").length;
  const cols: { k: string; v: (t: Trial) => number; f?: (t: Trial) => string; text?: boolean }[] = [
    { k: "Variantă", v: (t) => t.g.scheme.depth, f: (t) => optionName(t.g), text: true },
    { k: "Program", v: (t) => t.prog.serviceDepth, f: (t) => t.label, text: true },
    { k: "GLA m²", v: (t) => t.g.gla },
    { k: "Unități", v: (t) => t.g.scheme.units.length + t.g.scheme.pads.length },
    { k: "Locuri", v: (t) => t.g.parking.capacity },
    { k: "Chirii / an", v: (t) => t.pf.rentRoll, f: (t) => money(t.pf.rentRoll) },
    { k: "Profit", v: (t) => t.pf.profit, f: (t) => money(t.pf.profit) },
    { k: "Yield on cost %", v: (t) => t.pf.yieldOnCost, f: (t) => fmt(t.pf.yieldOnCost, 2) },
    { k: "Reguli încălcate", v: (t) => fails(t), f: (t) => (fails(t) ? `<span class="fail" title="${ctx.esc(t.g.checks.filter((c) => c.verdict === "fail").map((c) => c.rule).join(", "))}">${fails(t)}</span>` : "—") },
  ];
  let sortCol: number = ({ gla: 2, profit: 6, yoc: 7, parking: 4, units: 3 } as const)[ctx.state.retailSort];
  let desc = true;
  const dlg = document.createElement("dialog");
  dlg.className = "compare";
  const paint = () => {
    const c = cols[sortCol];
    // what complies comes first, whatever the column: a yard too short for the anchors' lorries is not a better scheme
    const best = [...pool].sort((a, b) => Number(fails(a) > 0) - Number(fails(b) > 0) || (desc ? -1 : 1) * (c.v(a) - c.v(b)) || b.pf.profit - a.pf.profit).slice(0, SHOW);
    const top = cols.map((x) => (x.text ? NaN : Math.max(...best.map(x.v))));
    dlg.innerHTML = `<div class="dhead"><b>Explorare: cele mai bune ${best.length} din ${tried} soluții</b><button class="btn" id="dclose">Închide</button></div>
      <table class="t"><tr>${cols.map((x, i) => `<th data-c="${i}" class="${i === sortCol ? "on" : ""} ${x.text ? "l" : ""}">${x.k}${i === sortCol ? (desc ? " ↓" : " ↑") : ""}</th>`).join("")}<th></th></tr>
      ${best.map((t, i) => `<tr>${cols.map((x, j) => {
        const v = x.v(t), shown = x.f ? x.f(t) : fmt(v);
        if (x.text) return `<td class="${j === 1 ? "src" : ""}">${shown}</td>`;
        return `<td class="n ${x.k === "Profit" ? (v >= 0 ? "pass" : "fail") : x.k !== "Reguli încălcate" && v === top[j] ? "best" : ""}">${shown}</td>`;
      }).join("")}<td><button class="btn" data-i="${i}">Aplică</button></td></tr>`).join("")}</table>
      <div class="sub">Se încearcă adâncimea rândului, adâncimea curții de livrare și numărul de pad-uri; terenul și regulile nu se schimbă. Variantele care respectă toate regulile verificate vin primele. „Aplică” pune programul în stânga.</div>`;
    dlg.querySelector("#dclose")!.addEventListener("click", () => dlg.close());
    dlg.querySelectorAll<HTMLElement>("th[data-c]").forEach((th) => (th.onclick = () => { const k = +th.dataset.c!; desc = k === sortCol ? !desc : true; sortCol = k; paint(); }));
    dlg.querySelectorAll<HTMLButtonElement>("button[data-i]").forEach((b) => (b.onclick = () => {
      const t = best[+b.dataset.i!];
      Object.assign(ctx.state.retail, { depths: t.prog.depths, serviceDepth: t.prog.serviceDepth, pads: t.prog.pads });
      ctx.state.optionKey = t.g.id;
      dlg.close();
      applied?.();
    }));
  };
  paint();
  dlg.addEventListener("close", () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
}
/** Called after "Aplică": the page rebuilds its controls and runs. */
export let applied: (() => void) | undefined;
export const onApplied = (f: () => void) => { applied = f; };

// ---------- report ----------

export function report() {
  if (!current || !result) return;
  const { fmt, money, esc, brand, state, ALL } = ctx;
  const g = current, s = g.scheme, f = pf(g), p = g.parking, b = g.balance;
  const shots = { park: ctx.capture(ALL, "3d"), site: ctx.capture(0, "plan") };
  const name = ctx.caseName();
  const kv = (k: string, v: string) => `<tr><td>${k}</td><td class="n">${v}</td></tr>`;
  const view = document.createElement("div");
  view.id = "reportView";
  view.innerHTML = `<div class="rbar"><button class="btn primary" id="rprint">Tipărește / salvează PDF</button><button class="btn" id="rclose">Închide</button></div>
    <article class="report">${brand.logo || brand.name ? `<div class="rbrand">${brand.logo ? `<img src="${brand.logo}" alt="">` : ""}<span>${esc(brand.name)}</span></div>` : ""}<h1>${esc(name)}</h1>
    <div class="sub">Retail park · ${optionName(g)} · generat ${new Date().toLocaleDateString("ro-RO")} · Development Assistant, versiunea ${ctx.commit}</div>
    <h2>Pe scurt</h2>
    <div class="kpis"><div>GLA<b>${fmt(g.gla)} m²</b></div><div>Unități<b>${s.units.length}${s.pads.length ? ` + ${s.pads.length} pad` : ""}</b></div>
      <div>Locuri de parcare<b>${p.capacity}</b></div><div>Yield on cost<b>${fmt(f.yieldOnCost, 2)}%</b></div></div>
    <h2>Masterplan</h2><img src="${shots.park}">
    <h2>Plan de situație</h2><img src="${shots.site}">
    <h2>Chiriași</h2><table>${g.byKind.map((k) => kv(KIND_NAME[k.kind], `${k.count} · ${fmt(k.gla)} m² GLA`)).join("")}${kv("<b>Total</b>", `${s.units.length + s.pads.length} · ${fmt(g.gla)} m² GLA`)}</table>
    <h2>Unități</h2><table>${s.units.map((u) => kv(`${u.id} · ${KIND_NAME[u.kind]}`, `${fmt(u.gla)} m² · ${fmt(u.frontage, 1)} × ${fmt(u.depth, 1)} m · ${u.service.filter((o) => o.kind === "dock").length} cheiuri`)).join("")}</table>
    <h2>Parcare</h2><table>${kv("După normă", String(p.byLaw))}${kv("Ținta chiriașilor", String(p.target))}${kv("Asigurat", `${p.capacity} (${fmt((p.capacity / Math.max(1, g.gla)) * 100, 1)} / 100 m² GLA)`)}${kv("Distanța maximă de mers", `${fmt(p.walkMax, 0)} m`)}</table>
    <h2>Bilanț teritorial</h2><table>${kv("Teren", fmt(b.plot) + " m²")}${kv("Construit", `${fmt(b.built)} m² (POT ${fmt(g.indicators.pot, 1)}%, cu copertina)`)}${kv("Parcare și drumuri", fmt(b.parking + b.roads) + " m²")}${kv("Curți de livrare", fmt(b.service) + " m²")}${kv("Spații verzi", `${fmt(b.green)} m² (${fmt((b.green / b.plot) * 100, 1)}%)`)}</table>
    <h2>Financiar</h2><table>${kv("Chirii pe an", money(f.rentRoll))}${kv("NOI", money(f.noi))}${kv(`Valoare la ${fmt(state.retailFinance.exitYield, 2)}%`, money(f.gdv))}${kv("Cost total", money(f.cost.total))}${kv("Profit · marjă", `${money(f.profit)} · ${fmt(f.margin * 100, 1)}%`)}${kv("Yield on cost", `${fmt(f.yieldOnCost, 2)}%`)}</table>
    <div class="note">Valorile financiare sunt cele introduse în aplicație; cele implicite sunt orientative.</div>
    <h2>Conformitate</h2><table>${g.checks.map((c) => `<tr><td>${c.rule}${c.verified ? "" : " <span class='note'>(regulă neverificată)</span>"}<div class="note">${c.source}</div></td><td class="n ${c.verdict}">${c.value}<div class="note">${c.limit}</div></td></tr>`).join("")}</table>
    <p class="note">Studiu preliminar generat automat, nu proiect. Accesele din drumul public cer avizul administratorului drumului; regulile marcate neverificat nu au fost citite în textul oficial.</p></article>`;
  document.body.appendChild(view);
  document.body.classList.add("report-open");
  const close = () => { view.remove(); document.body.classList.remove("report-open"); };
  view.querySelector<HTMLButtonElement>("#rclose")!.onclick = close;
  view.querySelector<HTMLButtonElement>("#rprint")!.onclick = () => window.print();
}

/** The figures a saved project shows in the list. */
export const summary = () => (current ? { gla: current.gla, units: current.scheme.units.length, profit: pf(current).profit } : null);
export const envelopeOf = (): Envelope | null => result?.envelope ?? null;
export const clear = () => { result = null; current = null; };
